<template>
  <div class="d-flex flex-column">

    <!-- Source info -->
    <InfoRow label="Source">
      <StatusBadge variant="secondary" class="d-flex align-items-center gap-1">
        <img v-if="listing.iconUrl" :src="listing.iconUrl" alt="" class="source-icon" height="16" />
        <span>{{ listing.listingSource.name }}</span>
      </StatusBadge>
      <template #action>
        <a :href="safeHref(listing.url)" target="source-listing" class="btn btn-sm btn-outline-secondary" rel="noopener noreferrer">
          <i class="bi-box-arrow-up-right me-1" />View
        </a>
      </template>
    </InfoRow>

    <!-- First found -->
    <InfoRow label="Found">
      <StatusBadge variant="secondary">
        {{ formatDate(listing.dateFirstFound) || 'Unknown' }}
      </StatusBadge>
    </InfoRow>

    <!-- Active status -->
    <InfoRow label="Status">
      <StatusBadge v-if="listing.isInactive" variant="danger">Inactive</StatusBadge>
      <StatusBadge v-else variant="success">Active</StatusBadge>
      <template #action>
        <button v-if="listing.data.listingPresence?.needsReview && !listing.isInactive"
                type="button" class="btn btn-sm btn-outline-success me-2" @click="listing.updateActiveStatus(true)">
          Keep active
        </button>
        <button
          v-if="listing.isInactive"
          type="button"
          :title="`This is back up on ${ listing.listingSource.name }`"
          class="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 text-start listing-status__wrap-button"
          @click="listing.updateActiveStatus(true)">
          <i class="bi-check" />
          <span>Listing is active</span>
        </button>
        <button
          v-else
          type="button"
          :title="`This is no longer on ${ listing.listingSource.name }`"
          class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 text-start listing-status__wrap-button"
          @click="listing.updateActiveStatus(false)">
          <i class="bi-x" />
          <span>Listing is not active</span>
        </button>
      </template>
    </InfoRow>

    <ListingPresenceNotice :presence="listing.data.listingPresence" :deactivation="listing.data.lastAbsenceDeactivation"
                           :inactive="listing.isInactive" :manual-override="Boolean(listing.data.statusOverride)" />

    <!-- Correct city -->
    <InfoRow label="Location">
      <template v-if="listing.isDefinitelyInCorrectCity">
        <StatusBadge variant="success">In {{ listing.city.name }}</StatusBadge>
      </template>
      <template v-else>
        <StatusBadge variant="warning">In another municipality</StatusBadge>
      </template>
      <template #action>
        <button
          v-if="listing.isDefinitelyInCorrectCity"
          type="button"
          :title="`This is not in ${ listing.city.name }`"
          class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 text-start listing-status__wrap-button"
          @click="listing.updateIsInCorrectCity(false)">
          <i class="bi-x" />
          <span>Listing is not in {{ listing.city.name }}</span>
        </button>
        <button
          v-else
          type="button"
          :title="`This is in ${ listing.city.name }`"
          class="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 text-start listing-status__wrap-button"
          @click="listing.updateIsInCorrectCity(true)">
          <i class="bi-check" />
          <span>Listing is in {{ listing.city.name }}</span>
        </button>
      </template>
    </InfoRow>

    <!-- Evidence / Screenshot -->
    <InfoRow label="Evidence" verticalAlign="start">
      <EvidenceStatus
        class="w-100"
        :evidence="listing.evidence"
        :city-id="listing.city.id"
        :listing-id="listing.id"
        @updated="onEvidenceUpdated"
      />
    </InfoRow>

  </div>
</template>

<script>
import { safeHref } from '@/safeRendering';
import {Listing} from "@/models/Listing";
import StatusBadge from "@/components/common/StatusBadge.vue";
import InfoRow from "@/components/common/InfoRow.vue";
import EvidenceStatus from "@/components/letters/EvidenceStatus.vue";
import ListingPresenceNotice from './ListingPresenceNotice.vue';

export default {
  components: {EvidenceStatus, InfoRow, StatusBadge, ListingPresenceNotice},
  props: {
    listing: Listing
  },
  methods: {
    safeHref,

    async onEvidenceUpdated() {
      await this.listing.reload();
    }
  }
}
</script>

<style scoped>
.source-icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.listing-status__wrap-button {
  white-space: normal;
}

@media (max-width: 991.98px) {
  .listing-status__wrap-button {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
